import React, { useEffect } from "react"
import { Tabs } from "antd"
import { useOutletContext, useParams, useNavigate } from "react-router-dom"

import wait from "@helpers/wait"
import { W1, isMobile } from "@components/constants"
import { INVESTMENT_STATUS_REJECTED, PROJECT_STATES,NOT_FOUND_URL } from "@components/Domain/Constants"

import InvestmentUpdateTab from "./InvestmentUpdateTab"
import InvestmentCreateTab from "./InvestmentCreateTab"

import "./InvestmentProfilePage.css"

const LABEL_PROFILE = "Profile"
const ACTION_CREATE = "create"


const InvestmentProfilesPage = () => {
  const navigate = useNavigate()
  const { investments, deleteInvestment, currentForm, project, getInvestment } = useOutletContext()
  const { projectId, action, investmentId: defaultInvestmentId } = useParams()

  const isCreateAction = action === ACTION_CREATE
  let activeKey = defaultInvestmentId || (isCreateAction ? ACTION_CREATE : undefined)

  const hasInvestments = !!investments && investments.length > 0
  const { state: projectState } = project

  const isProjectClosed = projectState === PROJECT_STATES.CLOSED

  let isInProgress = false
  useEffect(() => {
    if (isInProgress) { return }
    isInProgress = true

    if (!hasInvestments) {
      navigate(`/investments/${projectId}/create`)
    }
  }, [])

  useEffect(() => {
    if (defaultInvestmentId) {
      const investment = getInvestment(defaultInvestmentId)

      if (!investment) {
        return navigate(NOT_FOUND_URL)
      }
    }
  }, [investments])

  const openCreateTab = async () => {
    window.scrollTo({ top: 0 })
    navigate(`/investments/${projectId}/create`)

    await wait(250)
    activeKey = ACTION_CREATE
  }

  const onCreateCancel = () => {
    const activeInvestment = investments[investments.length - 1]
    const { id } = activeInvestment

    activeKey = id
    navigate(`/investments/${projectId}/update`)
  }

  const onChange = key => {
    currentForm.current.submit()
    navigate(`/investments/${projectId}/update/${key}`)
  }

  const onDelete = async investmentId => {
    const activeInvestment = investments
      .find(investment => investment.id !== investmentId)

    deleteInvestment(investmentId)

    if (!activeInvestment) {
      return openCreateTab()
    }

    const {id: key} = activeInvestment
    activeKey = key
    navigate(`/investments/${projectId}/update/${key}`)
  }

  const currentFormKey = activeKey || investments[0]?.id
  const isUpdateDeletable = hasInvestments

  const getInvestmentLabel = (investment, index) => {
    const label = `${LABEL_PROFILE} ${index + 1}`

    const isRejected = isInvestmentDeclined(investment)

    return isRejected ? `${label} (Declined)` : label
  }

  const isInvestmentDeclined = ({ status }) => status === INVESTMENT_STATUS_REJECTED

  const isInvestmentDisabled = investment => {
    if (!isProjectClosed) {
      return isInvestmentDeclined(investment)
    }

    return true
  }

  const getNextInvestmentId = key => {
    const currentInvestmentIndex = investments.findIndex(({ id }) => id === key)
    const nextInvestmentIndex = currentInvestmentIndex + 1
    const nextInvestment = investments[nextInvestmentIndex] || {}

    const { id } = nextInvestment

    return id
  }
  const isLastInvestment = key => !getNextInvestmentId(key)

  const items =
    investments.map((investment, index) => (
      {
        key: investment.id,
        label: getInvestmentLabel(investment, index),
        disabled: isInvestmentDisabled(investment),
        children: (
          <InvestmentUpdateTab
            index={index + 1}
            onDelete={onDelete}
            investment={investment}
            isDeletable={isUpdateDeletable}
            currentForm={currentFormKey === investment.id ? currentForm : undefined}
            openCreateTab={openCreateTab}
            nextInvestmentId={getNextInvestmentId(investment.id)}
            isLastInvestment={isLastInvestment(investment.id)}
          />
        )
      }
    ))

  if (isCreateAction) {
    const investmentsCount = investments.length
    const isCreateDeletable = hasInvestments

    items.push({
      key: ACTION_CREATE,
      label: `${LABEL_PROFILE} ${investmentsCount + 1}`,
      children: (
        <InvestmentCreateTab
          index={investmentsCount + 1}
          onCancel={onCreateCancel}
          isDeletable={isCreateDeletable}
          currentForm={currentForm}
          openCreateTab={openCreateTab}
        />
      )
    })
  }

  const tabBarStyle = {
    marginBottom: isMobile ? 0 : W1,
  }

  return (
    <Tabs
      size={ isMobile ? "small" : "medium" }
      items={items}
      onChange={onChange}
      activeKey={activeKey}
      className={isMobile ? "tabs-mobile" : ""}
      tabBarStyle={tabBarStyle}
      destroyInactiveTabPane
    />
  )
}

export default InvestmentProfilesPage
